import { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import EnumService from '../services/EnumService';
import { CompanyRes, StateRes } from '../proxy/proxy';
import LocalStorageService from '../services/LocalStorageService';

const localStorageService = LocalStorageService.getService();

export default function useStatesFilter() {
  const [states, setStates] = useState<StateRes[]>([]);
  const [selectedStates, setSelectedStates] = useState<number[]>([]);

  useEffect(() => {
    EnumService.getEnums('states').then((data: StateRes[]) => {
      setStates(data || []);
      const companyProfile: CompanyRes = localStorageService.getCompanyProfile();

      // Collect all state IDs in a single flat map pass (avoids deep nesting)
      const stateIds = companyProfile?.extCompanies
        ?.flatMap((extCompany) =>
          extCompany?.extForms
            ?.map((form) => form?.state?.id)
            .filter((id) => !!id)).filter(Boolean) || [];

      // Select only the states that match the IDs
      const selected = data
        .map((s) => s.id ?? 0)
        .filter((id) => stateIds.includes(id));

      setSelectedStates(selected);
    });
  }, []);

  const handleChange = ({ target: { value } }: any) => {
    setSelectedStates(typeof value === 'string' ? value.split(',') : value);
  };

  const StatesDropdown = (
    <FormControl
      size="small"
      sx={{
        minWidth: { xs: '100%', sm: 150 },
        maxWidth: { xs: '100%', sm: 150 },
        width: { xs: '100%', sm: 'auto' }
      }}
      fullWidth
    >
      <InputLabel id="states-filter-label">States</InputLabel>
      <Select
        labelId="states-filter-label"
        id="states-filter"
        multiple
        value={selectedStates}
        onChange={handleChange}
        input={<OutlinedInput label="States" />}
        fullWidth
        renderValue={(selected) =>
          states
            .filter((s) => s.id !== undefined && selected.includes(s.id))
            .map((s) => s.value)
            .join(', ')
        }
      >
        {states.map((state) => (
          <MenuItem key={state.id} value={state.id}>
            <Checkbox checked={selectedStates.indexOf(state.id ?? 0) > -1} />
            <ListItemText primary={state.value} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return { StatesDropdown, selectedStates, setSelectedStates, states };
}
